import React, { type ReactNode } from 'react';
import clsx from 'clsx';
import IconInformationCircle from '@/assets/icons/icon-information-circle.svg?react';
import {
  Tooltip as TooltipWrapper,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface InfoTooltipProps {
  text: string | ReactNode;
  className?: string;
  iconClassName?: string;
  contentClassName?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  ariaLabel?: string;
}

const renderContent = (content: string | ReactNode) => {
  if (typeof content === 'string') {
    const parts = content.split(/<br\s*\/?>|\n/gi);
    return parts.map((part, idx) => (
      <React.Fragment key={idx}>
        {part}
        {idx < parts.length - 1 && <br />}
      </React.Fragment>
    ));
  }
  return content;
};

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  text,
  className,
  iconClassName,
  contentClassName,
  position = 'top',
  ariaLabel = 'More information',
}) => {
  return (
    <TooltipProvider>
      <TooltipWrapper>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            className={clsx('inline-flex items-center', className)}
          >
            <IconInformationCircle className={clsx('h-4 w-4 text-neutral-300', iconClassName)} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side={position}
          className={clsx(
            'text-body-xs max-w-[240px] text-balance whitespace-pre-line text-white',
            contentClassName,
          )}
        >
          {renderContent(text)}
        </TooltipContent>
      </TooltipWrapper>
    </TooltipProvider>
  );
};
